import type { Listing } from '@seller-portal/types';

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

export function price(value: number | null, currency: string | null): string {
  if (value === null) return '—';
  if (!currency || currency === 'USD') return money.format(value);
  return `${value.toFixed(2)} ${currency}`;
}

export function count(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function stock(listing: Listing): string {
  if (listing.availability === 'in_stock' || listing.inStock) return 'In stock';
  if (listing.availability === null) return '—';
  return listing.availability.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export function since(iso: string | null, now = new Date()): string {
  if (!iso) return '—';
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return 'today';
  if (days < 30) return `${days} d ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)} y ago`;
}

/** A recent time in words: "just now", "12 min ago", "3 h ago", then as `since` does. */
export function ago(iso: string | null, now = new Date()): string {
  if (!iso) return '—';
  const minutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ago`;
  return since(iso, now);
}

/** The variant key as one line: "16 GB · 512 GB · 14 in". */
export function variantText(listing: Listing): string {
  return listing.variantKey.map((axis) => axis.value).join(' · ');
}

export function axisLabel(axis: string): string {
  return axis.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** When something happened, for activity lists: "Today, 14:05", "Yesterday, 09:12", or "27 Oct, 14:05". */
export function when(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(at)) / 86_400_000);
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Yesterday, ${time}`;
  return `${at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${time}`;
}
